import React, { useEffect, useRef, useState } from 'react';

// Util
import { getExternalLinkIcon, shouldOpenNewTab } from 'components/navV3/PostAuth/utils';
import { VerticalNavData } from 'components/navV3/PostAuth/verticalNavData';

// Styles
import {
  LinkWrapper,
  PrimaryLink,
  SecondaryLink,
  LinkIcon,
  LinkName,
  StyledTooltip,
} from './VerticalNavLink.styles';

type VerticalNavLinkProps = {
  active?: boolean;
  handleCloseNav?: () => void;
  handleOpenNav?: () => void;
  isLastLink?: boolean;
  link: VerticalNavData;
  navOpened?: boolean;
  type?: 'primary' | 'secondary';
  onClick: () => void;
};

const VerticalNavLink = ({
  active = false,
  handleCloseNav = () => undefined,
  handleOpenNav = () => undefined,
  isLastLink = false,
  link,
  navOpened = false,
  type = 'primary',
  onClick,
}: VerticalNavLinkProps): JSX.Element => {
  const { href, icon, isProductActivated, name, tooltipText } = link;
  const shouldShowTooltip = !isProductActivated && tooltipText;
  const isSecondaryLink = type === 'secondary';

  const linkWrapperRef = useRef<HTMLDivElement>(null);
  const showTooltipDelayTimeout = useRef<number>();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTop, setTooltipTop] = useState<number | undefined>();
  const SHOW_TOOLTIP_DELAY_MS = 600;

  useEffect(() => {
    if (!linkWrapperRef.current) return;
    setTooltipTop(linkWrapperRef.current.offsetTop - 14);
  }, [tooltipTop]);

  const handleShowTooltip = () => {
    if (shouldShowTooltip) {
      showTooltipDelayTimeout.current = window.setTimeout(
        () => setShowTooltip(true),
        navOpened ? 0 : SHOW_TOOLTIP_DELAY_MS // no delay if vertical nav is opened
      );
      return showTooltipDelayTimeout;
    }
    return null;
  };

  const handleHideTooltip = () => {
    clearTimeout(showTooltipDelayTimeout.current);
    setShowTooltip(false);
  };

  useEffect(
    () => () => {
      clearTimeout(showTooltipDelayTimeout.current);
    },
    [showTooltipDelayTimeout]
  );

  return (
    <LinkWrapper ref={linkWrapperRef} secondary={isSecondaryLink}>
      {isSecondaryLink ? (
        <SecondaryLink
          href={href}
          noTextTransform
          noUnderline
          onBlur={() => {
            if (isLastLink) handleCloseNav();
          }}
          onClick={onClick}
          {...(shouldOpenNewTab(name) && { target: '_blank' })}
        >
          <LinkName active={active} open={navOpened}>
            {name}
          </LinkName>
          {getExternalLinkIcon(name)}
        </SecondaryLink>
      ) : (
        <PrimaryLink
          active={active}
          href={href}
          noTextTransform
          noUnderline
          onBlur={handleHideTooltip}
          onKeyUp={() => {
            handleShowTooltip();
            if (isLastLink) handleOpenNav();
          }}
          onMouseEnter={handleShowTooltip}
          onMouseLeave={handleHideTooltip}
          onClick={onClick}
        >
          <LinkIcon>{icon}</LinkIcon>
          <LinkName active={active} open={navOpened}>
            {name}
          </LinkName>
          <StyledTooltip popIn text={tooltipText} show={showTooltip} top={tooltipTop} />
        </PrimaryLink>
      )}
    </LinkWrapper>
  );
};

export default VerticalNavLink;
